// src/components/authoring/MarkupItemDetail.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Chip,
  Checkbox,
  ListItemText,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import { useParams, useLocation } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import { BLOOMS_TAXONOMY_SKILLS } from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
import { ArabicGrammarTerms } from "../utils/ArabicGrammarTerms";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";
import getAuthoringPreviewProps from "./getAuthoringPreviewProps";
import ActivityRenderer from "../../items/ActivityRenderer";

const colorMap = [
  "#e74c3c",
  "#27ae60",
  "#3498db",
  "#7f8c8d",
  "#f1c40f",
  "#8e44ad",
  "#2980b9",
  "#1abc9c",
  "#c0392b",
  "#9b59b6",
];

const createEmptyMarkupItem = () => ({
  version: "v1",
  dateCreated: new Date().toISOString(),
  dateUpdated: new Date().toISOString(),
  createdBy: "",
  updatedBy: "",
  Type: "Markup",
  Name: "",
  Title: "",
  Text: "",
  MarkupType: "Underline",
  Direction: "RTL",
  points: 1,
  skilltype: "",
  itemcat: "",
  Options: {},
});

const MarkupItemDetail = () => {
  const { itemId } = useParams();
  const location = useLocation();

  // 🔥 detect cloned item
  const clonedItem = location.state?.clonedItem || null;

  // 🔥 FORCE create mode if cloned OR URL is "new"
  const isNew = clonedItem ? true : !itemId || itemId === "new";

  const [item, setItem] = useState(createEmptyMarkupItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [rationaleText, setRationaleText] = useState("");

  // -------------------------------
  // LOAD ITEM (Supports Clone Mode)
  // -------------------------------
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          clonedItem
            ? Promise.resolve({ item: clonedItem })
            : isNew
            ? Promise.resolve(null)
            : ItemService.fetchItemById(itemId),
        ]);

        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        let loadedItem = null;

        // 🔥 If cloned item is provided, sanitize it
        if (clonedItem) {
          loadedItem = {
            ...clonedItem,
            id: undefined,
            Id: undefined,
            itemId: undefined,
            ItemId: undefined,
            createdBy: "",
            updatedBy: "",
            dateCreated: new Date().toISOString(),
            dateUpdated: new Date().toISOString(),
            version: "v1",
            json: undefined,
            Json: undefined,
          };
        } else if (itemResponse?.item) {
          loadedItem = itemResponse.item;
        } else {
          loadedItem = createEmptyMarkupItem();
        }

        // Convert numeric keys → words
        if (loadedItem.Options && loadedItem.Text) {
          const words = loadedItem.Text.split(" ").filter((w) => w.trim());
          const converted = {};

          Object.entries(loadedItem.Options).forEach(([key, value]) => {
            const index = parseInt(key, 10) - 1;
            if (words[index]) converted[words[index]] = value;
          });

          loadedItem.Options = converted;
        }

        setItem(loadedItem);
      } catch (err) {
        console.error("Failed to load:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [itemId, clonedItem, isNew]);

  const wordsFromText = useMemo(
    () => (item.Text ? item.Text.split(" ").filter((w) => w.trim()) : []),
    [item.Text]
  );

  // cleanup stale words
  useEffect(() => {
    setItem((prev) => {
      const validWords = new Set(wordsFromText);
      const cleaned = Object.fromEntries(
        Object.entries(prev.Options).filter(([word]) => validWords.has(word))
      );
      return { ...prev, Options: cleaned };
    });
  }, [wordsFromText]);

  // color mapping for preview
  const typeToColorMap = useMemo(() => {
    const map = {};
    const unique = new Set();

    Object.values(item.Options).forEach((vals) =>
      vals.forEach((v) => unique.add(v.Type))
    );

    [...unique].forEach((t, i) => {
      map[t] = colorMap[i % colorMap.length];
    });

    return map;
  }, [item.Options]);

  const handleFieldChange = (field, value) =>
    setItem((prev) => ({ ...prev, [field]: value }));

  const handleAddMapping = () => {
    if (!selectedWord || !selectedCategory || selectedValues.length === 0)
      return;

    setItem((prev) => {
      const prevOpt = { ...prev.Options };
      const existing = prevOpt[selectedWord] || [];

      const newEntries = selectedValues.map((val) => ({
        Type: val,
        Rationale: { Text: rationaleText },
      }));

      const updated = [
        ...existing.filter((e) => !selectedValues.includes(e.Type)),
        ...newEntries,
      ];

      return { ...prev, Options: { ...prevOpt, [selectedWord]: updated } };
    });

    setSelectedWord("");
    setSelectedCategory("");
    setSelectedValues([]);
    setRationaleText("");
  };

  const handleRemoveValue = (word, idx) => {
    setItem((prev) => {
      const copy = { ...prev.Options };
      copy[word].splice(idx, 1);
      if (copy[word].length === 0) delete copy[word];
      return { ...prev, Options: copy };
    });
  };

  const handleRationaleChange = (word, idx, text) => {
    setItem((prev) => {
      const copy = { ...prev.Options };
      copy[word][idx].Rationale.Text = text;
      return { ...prev, Options: copy };
    });
  };

  // convert Options back to numeric keys
  const numberedOptions = useMemo(() => {
    const opt = {};
    wordsFromText.forEach((word, index) => {
      if (item.Options[word]) opt[(index + 1).toString()] = item.Options[word];
    });
    return opt;
  }, [item.Options, wordsFromText]);

  const numberedItem = useMemo(
    () => ({ ...item, Options: numberedOptions }),
    [item, numberedOptions]
  );

  const jsonPreview = JSON.stringify(numberedItem, null, 2);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const payload = {
        ...numberedItem,
        Type: "Markup",
        id: undefined,
        dateUpdated: new Date().toISOString(),
      };

      await ItemService.saveItem(payload, isNew ? undefined : itemId);

      setSaveSuccess(true);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveError("Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [numberedItem, isNew, itemId]);

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary">
            Loading item details...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {/* BASIC INFO */}
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label={item.Type} color="secondary" />
              <Typography variant="h5">
                {item.Name || "Untitled Markup"}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setPreviewDialogOpen(true)}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                onClick={() => setJsonDialogOpen(true)}
              >
                View JSON
              </Button>
            </Stack>
          </Stack>

          {saveSuccess && (
            <Alert severity="success">Item saved successfully!</Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          <Divider />

          {/* FORM FIELDS */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                fullWidth
                value={item.Name}
                onChange={(e) => handleFieldChange("Name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                value={item.points}
                onChange={(e) =>
                  handleFieldChange("points", Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  onChange={(e) =>
                    handleFieldChange("skilltype", e.target.value)
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  onChange={(e) => handleFieldChange("itemcat", e.target.value)}
                >
                  {taxonomyOptions.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Markup Type */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Markup Type</InputLabel>
                <Select
                  value={item.MarkupType}
                  onChange={(e) =>
                    handleFieldChange("MarkupType", e.target.value)
                  }
                >
                  <MenuItem value="Underline">Underline</MenuItem>
                  <MenuItem value="Highlight">Highlight</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Direction */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={item.Direction}
                  onChange={(e) =>
                    handleFieldChange("Direction", e.target.value)
                  }
                >
                  <MenuItem value="RTL">RTL</MenuItem>
                  <MenuItem value="LTR">LTR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Title */}
          <Box>
            <Typography fontWeight={600}>Instructions</Typography>
            <TinyEditor
              value={item.Title}
              direction={item.Direction}
              onChange={(v) => handleFieldChange("Title", v)}
            />
          </Box>

          {/* Text */}
          <Box>
            <Typography fontWeight={600}>Text</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={item.Text}
              onChange={(e) => handleFieldChange("Text", e.target.value)}
            />
          </Box>
        </Stack>
      </Paper>

      {/* WORD MAPPING */}
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={600}>
            Word Grammar Mapping
          </Typography>

          {/* Mapping Form */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Word</InputLabel>
                <Select
                  value={selectedWord}
                  onChange={(e) => setSelectedWord(e.target.value)}
                >
                  {wordsFromText.map((w, i) => (
                    <MenuItem key={i} value={w}>
                      {w}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {Object.keys(ArabicGrammarTerms).map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!selectedCategory}>
                <InputLabel>Values</InputLabel>
                <Select
                  multiple
                  value={selectedValues}
                  onChange={(e) => setSelectedValues(e.target.value)}
                  renderValue={(vals) => vals.join(", ")}
                >
                  {selectedCategory &&
                    ArabicGrammarTerms[selectedCategory].map((v) => (
                      <MenuItem key={v.value} value={v.value}>
                        <Checkbox checked={selectedValues.includes(v.value)} />
                        <ListItemText primary={v.label} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMapping}
            disabled={
              !selectedWord || !selectedCategory || selectedValues.length === 0
            }
          >
            Add Mapping
          </Button>

          {/* Existing Mappings */}
          {Object.entries(item.Options).map(([word, values]) => (
            <Paper key={word} variant="outlined" sx={{ p: 2 }}>
              <Typography fontWeight={600}>{word}</Typography>

              {values.map((v, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>
                      <b>Type:</b> {v.Type}
                    </Typography>

                    <IconButton
                      color="error"
                      onClick={() => handleRemoveValue(word, idx)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>

                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Rationale"
                    value={v.Rationale?.Text || ""}
                    onChange={(e) =>
                      handleRationaleChange(word, idx, e.target.value)
                    }
                    sx={{ mt: 1 }}
                  />
                </Box>
              ))}
            </Paper>
          ))}

          {/* SAVE BUTTON */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={isNew ? <AddIcon /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? isNew
                  ? "Creating..."
                  : "Saving..."
                : isNew
                ? "Create"
                : "Save"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* PREVIEW */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Preview</DialogTitle>
        <DialogContent dividers>
          <DialogContent dividers>
            <ActivityRenderer
              {...getAuthoringPreviewProps(item, typeToColorMap)}
            />
          </DialogContent>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* JSON */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MarkupItemDetail;
